/** Whether the project uses a standard basemap or a custom raster image as background */
export type MapType = 'map' | 'image';

/**
 * Represents a patch to apply to a base MapLibre style.
 * Contains paint and layout modifications for specific layers.
 *
 * Legacy-формат кастомных стилей времён OFM-тайлов (id слоёв maptiler-basic и др.).
 * Новые стили сохраняются как {@link MapFlavorStyle}; старые конвертируются на лету
 * (`convertLegacyPatchToFlavorStyle` в apps/web).
 */
export interface MapStylePatch {
  /**
   * Map of layer IDs to their paint/layout overrides.
   * Only the specified properties will be applied; the rest of the layer remains unchanged.
   */
  layers: Record<string, {
    paint?: Record<string, unknown>;
    layout?: Record<string, unknown>;
  }>;
  /** Опционально: происхождение патча (AI / ручной редактор групп). */
  meta?: { kind?: 'ai' | 'manual'; version?: 1 };
}

/** Именованный флейвор protomaps, служащий базой кастомного стиля. */
export type MapFlavorBase = 'light' | 'dark' | 'white' | 'black' | 'grayscale';

/**
 * Leaf-категории слоёв basemap, которые можно скрывать в кастомном стиле.
 * Хранятся в `MapFlavorStyle.hidden`. Родительские категории UI (labels, roads)
 * не хранятся — раскрываются в leaf-ключи. Legacy-ключи первых flavor-стилей
 * (`admin`, `rail`, `oneway`, `road_labels`, `place_labels`, `labels`) конвертируются на лету.
 */
export type MapFlavorHiddenCategory =
  // актуальные leaf-ключи
  | 'water'
  | 'nature'
  | 'land'
  | 'buildings'
  | 'roads.network'
  | 'roads.rail'
  | 'roads.runway'
  | 'roads.oneway'
  | 'boundaries'
  | 'poi'
  | 'labels.places'
  | 'labels.roads'
  | 'labels.water'
  | 'labels.address'
  | 'labels.islands'
  // legacy-ключи (конвертируются)
  | 'labels'
  | 'admin'
  | 'rail'
  | 'oneway'
  | 'road_labels'
  | 'place_labels';

/**
 * Кастомный стиль базовой карты на protomaps: переопределения цветовых полей
 * `Flavor` поверх именованного флейвора + скрытые категории слоёв.
 * Слои стиля генерируются пакетом `@protomaps/basemaps` — id слоёв наружу не выходят.
 */
export interface MapFlavorStyle {
  kind: 'flavor';
  version: 1;
  base: MapFlavorBase;
  /**
   * Цвета по полям Flavor (`background`, `water`, `buildings`, `highway`, …).
   * Вложенные поля — через точку (`landcover.forest`). Значения — CSS-цвета (#hex / rgba).
   */
  overrides: Record<string, string>;
  /** Скрытые категории слоёв (visibility: none). */
  hidden?: MapFlavorHiddenCategory[];
  /** Происхождение (AI / ручной редактор). */
  meta?: { kind?: 'ai' | 'manual' };
}

/** Данные кастомного стиля в БД/настройках: новый flavor-формат или legacy-патч. */
export type CustomMapStyleData = MapFlavorStyle | MapStylePatch;

/** Type guard нового flavor-формата кастомного стиля. */
export function isMapFlavorStyle(value: unknown): value is MapFlavorStyle {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { kind?: unknown }).kind === 'flavor' &&
    typeof (value as { overrides?: unknown }).overrides === 'object'
  );
}

/** Configuration for a raster image used as the map background */
export interface BackgroundImageConfig {
  /** URL to the original uploaded image, e.g. /uploads/xxx.png */
  imageUrl: string;
  /** URL template for generated XYZ tiles, e.g. /uploads/tiles/bg-xxx/{z}/{x}/{y}.png */
  tilesUrl?: string;
  /** Tile bounds [west, south, east, north] */
  tileBounds?: [number, number, number, number];
  tileMinZoom?: number;
  tileMaxZoom?: number;
  /** Original image dimensions in pixels */
  width: number;
  height: number;
  /** Corner coordinates in lng/lat: [TL, TR, BR, BL] */
  coordinates: number[][];
}

export interface MapSettings {
  center: [number, number];
  zoom: number;
  pitch: number;
  bearing: number;
  bounds?: [[number, number], [number, number]];
  /** Minimum zoom level the user can zoom out to (0–24) */
  minZoom?: number;
  /** Maximum zoom level the user can zoom in to (0–24) */
  maxZoom?: number;
  /**
   * For image-map mode: how much padding to add around the image before clamping
   * the map viewport. Expressed as a fraction of the image extent — e.g. 0.5 means
   * 50 % of the image width/height is added on each side (default: 0.5).
   * Set to 0 to disable panning beyond the image boundary.
   */
  imageBoundaryPadding?: number;
  /** Type of map background — standard basemap or custom image (default: 'map') */
  mapType?: MapType;
  /** Background image configuration (only when mapType === 'image') */
  backgroundImage?: BackgroundImageConfig;
  /** Background color behind the image (hex string, default '#e0e0e0') */
  backgroundColor?: string;
  /** Whether to show zoom and compass navigation controls on the public map (default: true) */
  showControls?: boolean;
  /**
   * If true, user cannot rotate or tilt the map (mouse, touch, keyboard);
   * compass control is hidden when controls are shown.
   */
  lockPitchAndBearing?: boolean;
  /** Selected base map style key — protomaps flavor (light, dark, white, black, grayscale) или legacy OFM-ключ. Default: 'light' */
  mapStyleKey?: string;
  /** Данные активного кастомного стиля (flavor или legacy-патч) */
  customMapStylePatch?: CustomMapStyleData;
  /** Name of the custom AI-generated map style (e.g., "Dark Forest") */
  customMapStyleName?: string;
  /** Default font for map labels (text-font in symbol layers). null = use system default */
  defaultFont?: string | null;
  /** Настройки режима «Креатив» (упрощённый редактор с наборами оформления). */
  creator?: CreatorSettings;
}

/** Состояние режима «Креатив» у проекта. Появляется при первом применении набора. */
export interface CreatorSettings {
  /** Активный набор оформления (id из реестра пресетов приложения). */
  presetId: string;
  /** Когда набор применили (ISO) — диагностика. */
  appliedAt: string;
}

/** Вид левой панели публичного вьювера. */
export type ViewerPanelMode = 'filters' | 'cards';

/** Раскладка строки в карточном списке вьювера (референс pampam «List layout»). */
export type ViewerListLayout = 'medium' | 'large' | 'small' | 'no-image';

/**
 * Язык подписей базовой карты на публичной карте.
 * 'auto' — по языку интерфейса посетителя; 'ru'/'en' — жёстко задан владельцем.
 * Влияет только на подписи basemap, интерфейс вьювера остаётся на языке посетителя.
 */
export type ViewerMapLanguage = 'auto' | 'ru' | 'en';

/**
 * Настройки отображения публичного вьювера (диалог «Настройки» в редакторе/креативе):
 * попапы у точки, раскладка карточного списка, поля строк. Всё опционально —
 * дефолты собирает `resolveViewerDisplaySettings`.
 */
export interface ViewerDisplaySettings {
  /** Карточка объекта у точки на карте вместо панели справа (pampam «Popup modals»). */
  popupModals?: boolean;
  /** Язык подписей базовой карты. Отсутствует → 'auto' (язык интерфейса посетителя). */
  mapLanguage?: ViewerMapLanguage;
  list?: {
    /** Раскладка строк списка. Default: 'medium'. */
    layout?: ViewerListLayout;
    /**
     * Имя свойства для основной строки под названием ('' — не показывать).
     * Отсутствует → свойство типа markdown («Описание»).
     */
    primary?: string;
    /** Имя свойства второй строки. Отсутствует/'' — второй строки нет. */
    secondary?: string;
    /** Кнопки-ссылки (свойства типа link) в строках списка. Default: false. */
    showButtons?: boolean;
  };
}

export interface ShareSettings {
  showFilter: boolean;
  showSearch: boolean;
  hiddenLayerIds: string[];
  boundaryRestriction?: [[number, number], [number, number]];
  defaultPitch: number;
  defaultBearing: number;
  defaultZoom: number;
  /** UI color theme: 'auto' follows the map style, 'dark'/'light' are manual overrides */
  uiTheme?: 'auto' | 'light' | 'dark';
  /**
   * Visual theme preset for the public viewer UI.
   * Each preset corresponds to a pair of CSS classes in viewer-themes.css:
   *   .vt-{preset}-dark  and  .vt-{preset}-light
   * Defaults to 'default' when omitted.
   */
  uiThemePreset?: string;
  /** Custom font family for headings in the viewer UI (panel title, card title). null = system default */
  headingFont?: string | null;
  /** Custom font family for body text in the viewer UI (labels, values, descriptions). null = system default */
  bodyFont?: string | null;
  /** When true, visitors with the share link can create new root comments (subject to rate limits). */
  allowPublicComments?: boolean;
  /** When true, visitors see comment markers on the public map (read). */
  showPublicComments?: boolean;
  /** When true, visitors can reply to root comments on the public map. Requires `showPublicComments`. */
  allowPublicReplies?: boolean;
  /**
   * Скрыть лого «Слоя» на публичной карте. Платная возможность (`branding_optional`,
   * docs/billing/plan.md §6): включение гейтится тарифом на сервере, а действует флаг
   * только пока тариф её даёт — при даунгрейде лого возвращается без правки настроек.
   */
  hideSloyBrand?: boolean;
  /**
   * Панель публичного вьювера: 'cards' — карточный список мест (режим «Креатив»),
   * 'filters' — слои с фильтрами и легендами. Отсутствует → см. resolveViewerPanelMode.
   */
  panelMode?: ViewerPanelMode;
  /** Настройки отображения вьювера (диалог «Настройки»). Отсутствует → дефолты. */
  viewerDisplay?: ViewerDisplaySettings;
}

export interface Project {
  id: string;
  userId: string;
  name: string;
  description: string;
  mapStyle: Record<string, unknown>;
  settings: MapSettings;
  isPublic: boolean;
  shareToken: string | null;
  shareSettings: ShareSettings | null;
  createdAt: string;
  updatedAt: string;
  /** Role of the current user in this project (only present for shared projects) */
  role?: ProjectMemberRole | 'owner';
  /** Public URL to the latest dashboard preview (WebP snapshot captured in the editor). */
  previewUrl?: string | null;
  /** When the preview was last refreshed (ISO string), used for cache-busting and sorting. */
  previewUpdatedAt?: string | null;
}

/** Поля превью проекта (snapshot карты). Используется в Dashboard и viewer-types. */
export interface ProjectPreviewFields {
  /** Public URL к WebP-снэпшоту карты (null = превью ещё не снято). */
  previewUrl?: string | null;
  /** ISO-дата последнего обновления превью, используется для cache-busting. */
  previewUpdatedAt?: string | null;
}

/* ------------------------------------------------------------------ */
/*  Project Members                                                    */
/* ------------------------------------------------------------------ */

export type ProjectMemberRole = 'viewer' | 'editor';
export type ProjectMemberStatus = 'pending' | 'active';

export interface ProjectMember {
  id: string;
  projectId: string;
  userId: string | null;
  email: string;
  name: string | null;
  role: ProjectMemberRole;
  status: ProjectMemberStatus;
  createdAt: string;
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  mapStyle?: Record<string, unknown>;
  settings?: Partial<MapSettings>;
  shareSettings?: Partial<ShareSettings>;
}
